import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Services', href: '/services' },
  { name: 'About', href: '/about' },
  { name: 'Impact', href: '/impact' },
  { name: 'Blog', href: '/blog' },
  { name: 'Contact', href: '/contact' },
];

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const location = useLocation();

  useEffect(() => { setIsMounted(true); }, []);
  useEffect(() => { setIsMobileMenuOpen(false); }, [location]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/90 backdrop-blur-xl shadow-lg shadow-gray-900/5 border-b border-white/20' : 'bg-white border-b border-gray-100'
    }`}>
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Brand with logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <picture>
              <source srcSet="/images/logo.webp" type="image/webp" />
              <img
                src="/images/logo.png"
                alt="Punarvaa logo"
                width={36}
                height={36}
                className="w-9 h-9 object-contain"
                loading="eager"
              />
            </picture>
            <div className="flex flex-col leading-none">
              <span className="text-base font-bold text-gray-900 tracking-tight">Punarvaa</span>
              <span className="text-[10px] text-gray-400 font-normal">by Patekar Recycle</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm transition-colors pb-0.5 border-b-2 ${
                  location.pathname === item.href
                    ? 'text-p-600 border-p-600 font-medium'
                    : 'text-gray-600 border-transparent hover:text-p-600 hover:border-p-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA + Phone */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+919320937373"
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-p-600 transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              93209 37373
            </a>
            <Link
              to="/contact?quote=true"
              className="px-5 py-2 bg-p-600 text-white text-sm font-medium rounded-md hover:bg-p-700 transition-colors"
            >
              Get Quote →
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMounted && isMobileMenuOpen && (
          <div className="lg:hidden absolute left-0 right-0 top-16 bg-white border-b border-gray-200 shadow-lg px-4 sm:px-6 pb-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block py-3 text-sm border-b border-gray-100 transition-colors ${
                  location.pathname === item.href
                    ? 'text-p-600 font-medium'
                    : 'text-gray-600 hover:text-p-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <a href="tel:+919320937373" className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" /> 93209 37373
              </a>
              <Link
                to="/contact?quote=true"
                className="block w-full px-5 py-2.5 text-center text-sm font-medium text-white bg-p-600 rounded-md hover:bg-p-700 transition-colors"
              >
                Get Quote →
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
